// frontend/src/utils/tssCyclingUtils.js
// ----------------------------------------------------
// Cálculo TSS para CICLISMO
// Prioridad:
// 1) Potencia -> IF = NP / FTP
// 2) Fallback HR -> IF = avgHR / HRmax
// Fórmula base: TSS = horas * IF^2 * 100
//
// Exporta calculateIntensityFactor para compatibilidad con CyclingMetricsTable
// ----------------------------------------------------

export const interpretTSS = (tssValue) => {
  if (tssValue < 50) return "Entrenamiento muy ligero.";
  if (tssValue < 100) return "Entrenamiento base.";
  if (tssValue < 150) return "Entrenamiento moderado.";
  if (tssValue < 200) return "Entrenamiento intenso.";
  return "Entrenamiento muy intenso.";
};

// ---------------- Helpers ----------------

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const normalizeDurationSeconds = (raw) => {
  const n = toNumber(raw);
  if (n <= 0) return 0;
  return n > 1_000_000 ? n / 1000 : n;
};

// ✅ COMPAT: exportado
export const calculateIntensityFactor = (numerator, denominator) => {
  const num = toNumber(numerator);
  const den = toNumber(denominator);
  if (!num || !den || den <= 0) return NaN;
  const IF = num / den;
  return Number.isFinite(IF) ? IF : NaN;
};

// ✅ COMPAT: exportado
export const calculateTSS = (durationHours, intensityFactor) => {
  const h = toNumber(durationHours);
  const IF = Number(intensityFactor);
  if (!Number.isFinite(h) || !Number.isFinite(IF) || h <= 0) return NaN;
  const tss = h * IF * IF * 100;
  return Number.isFinite(tss) ? tss : NaN;
};

// ---------------- Extracciones ----------------

const getSession = (data) =>
  data?.session || (Array.isArray(data?.sessions) ? data.sessions[0] : {}) || {};

const extractFTP = (data) => {
  const z = data?.zones_target;
  if (z && typeof z === "object") {
    return toNumber(z.functional_threshold_power || z.ftp);
  }
  return 0;
};

const extractHRmax = (data, session) => {
  return (
    toNumber(data?.zones_target?.max_heart_rate) ||
    toNumber(data?.profile?.hr_max) ||
    toNumber(data?.profile?.hrMax) ||
    toNumber(session?.max_heart_rate) ||
    0
  );
};

// ----------------------------------------------------

export const calculateTSSWithTerrainCycling = (data) => {
  const session = getSession(data);

  const durationSec = normalizeDurationSeconds(
    data?.activity?.totalTimerTime || session?.total_timer_time
  );

  if (!durationSec) {
    return { tss: NaN, message: "Duración inválida." };
  }

  const hours = durationSec / 3600;

  // ---------- 1) POTENCIA ----------
  const normalizedPower =
    toNumber(data?.activity?.normalizedPower) ||
    toNumber(session?.normalized_power);

  const ftp = extractFTP(data);

  if (normalizedPower > 0 && ftp > 0) {
    let IF = calculateIntensityFactor(normalizedPower, ftp);
    IF = clamp(IF, 0, 1.5);

    const tssVal = calculateTSS(hours, IF);

    return {
      tss: Number(tssVal.toFixed(2)),
      message: interpretTSS(tssVal),
      source: "power",
      IF: Number(IF.toFixed(2)),
      normalizedPower,
      ftp,
    };
  }

  // ---------- 2) FALLBACK HR ----------
  const avgHR = toNumber(session?.avg_heart_rate);
  const hrMax = extractHRmax(data, session);

  if (avgHR > 0 && hrMax > 0) {
    let IF = calculateIntensityFactor(avgHR, hrMax);
    IF = clamp(IF, 0, 1.0);

    const tssVal = calculateTSS(hours, IF);

    return {
      tss: Number(tssVal.toFixed(2)),
      message: interpretTSS(tssVal),
      source: "hr",
      IF: Number(IF.toFixed(2)),
      avgHR,
      hrMax,
    };
  }

  return { tss: NaN, message: "Datos insuficientes para TSS." };
};

export default calculateTSSWithTerrainCycling;
